import { Loader, Text } from "@mantine/core";
import { motion } from "motion/react";
import type Player from "video.js/dist/types/player";

import HeadphonesIcon from "~icons/basil/headphone-solid";
import PlayIcon from "~icons/heroicons/play-20-solid";

import VideoJS from "./VideoJS";

import classes from "./PatternsStoryPlayer.module.css";

export interface PatternsStoryPlayerProps extends Omit<BoxProps, "style"> {
  storyUrl: string;
  onCompleted: () => void;
  onAnimationStart?: () => void;
  onAnimationComplete?: () => void;
}

const PatternsStoryPlayer: FC<PatternsStoryPlayerProps> = ({
  storyUrl,
  onCompleted,
  onAnimationStart,
  onAnimationComplete,
  ...otherProps
}) => {
  // == Player
  const playerRef = useRef<Player | null>(null);
  const [playerReady, setPlayerReady] = useState(false);
  // const [playerDuration, setPlayerDuration] = useState<number | null>(null);
  // const [playerTimestamp, setPlayerTimestamp] = useState<number>(0);

  // == Status
  const [status, setStatus] = useState<
    "pending" | "started" | "paused" | "finished"
  >("pending");
  useEffect(() => {
    // Reset when finished
    return () => {
      setStatus("pending");
    };
  }, []);
  // const prevStatus = usePrevious(status);
  const updateStatus = useCallback(() => {
    const player = playerRef.current;
    if (player) {
      const currentTimeOrNaN = player.currentTime() ?? 0;
      const currentTime = Number.isFinite(currentTimeOrNaN)
        ? currentTimeOrNaN
        : 0;
      const durationOrNaN = player.duration() ?? 0;
      const duration = Number.isFinite(durationOrNaN) ? durationOrNaN : null;
      setStatus(
        currentTime >= (duration ?? 0) - 5
          ? "finished"
          : currentTime > 0
            ? player.paused()
              ? "paused"
              : "started"
            : "pending",
      );
    }
  }, []);

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1 }}
      {...{ onAnimationStart, onAnimationComplete }}
      {...otherProps}
    >
      <Box pos="relative" mx="auto" maw={340} style={{ alignSelf: "center" }}>
        <VideoJS
          options={{
            sources: [{ src: storyUrl }],
            fluid: true,
            disablePictureInPicture: true,
            preload: "auto",
            controls: true,
            bigPlayButton: false,
            controlBar: {
              fullscreenToggle: false,
            },
            aspectRatio: "9:16",
          }}
          mih={600}
          onReady={player => {
            // Mark player is ready when we get our first chunk
            player.on("loadeddata", (event: any) => {
              console.debug("Loaded data", event);
              setPlayerReady(true);
            });

            // Auto play/pause to match fullscreen state
            player.on("fullscreenchange", (event: any) => {
              if (player.isFullscreen() || player.isFullWindow) {
                console.debug("Entered fullscreen", event);
                if (player.paused()) {
                  void player.play();
                }
              } else {
                console.debug("Exited fullscreen", event);
                if (!player.paused()) {
                  player.pause();
                }
              }
            });

            // Prevent adjusting playback rate when possible
            player.on("playbackrateschange", (event: any) => {
              console.debug("Playback rate changed", event);
              player.playbackRate(player.defaultPlaybackRate());
            });

            // Fullscreen when playing
            player.on("play", (event: any) => {
              console.debug("Playing", event);
              if (!player.isFullscreen() || !player.isFullWindow) {
                void player.requestFullscreen();
              }
              updateStatus();
            });

            // Exit fullscreen when paused
            player.on("pause", (event: any) => {
              setTimeout(() => {
                if (!player.paused()) {
                  return;
                }
                console.debug("Paused", event);
                if (player.isFullWindow) {
                  player.exitFullWindow();
                } else if (player.isFullscreen()) {
                  void player.exitFullscreen();
                }
                updateStatus();
              }, 100);
            });

            player.on("ended", (event: any) => {
              console.debug("Ended", event);
              updateStatus();
            });

            // // Track seeking events
            // player.on("seeked", () => {
            //   if (isFsInitialized()) {
            //     void FullStory("trackEventAsync", {
            //       name: "story_perspective_seeked",
            //       properties: {
            //         story: story.id,
            //         perspective,
            //       },
            //     });
            //   }
            // });

            // // Update player info on dev panel
            // player.on("timeupdate", () => {
            //   setPlayerDuration(player.duration() ?? null);
            //   setPlayerTimestamp(player.currentTime() ?? 0);
            // });

            // Load first video
            player.load();
            playerRef.current = player;
          }}
        />
        <Stack
          align="center"
          justify="center"
          gap="xs"
          pos="absolute"
          inset={0}
          p="lg"
          style={{
            borderRadius: "var(--mantine-radius-default)",
            backdropFilter: "blur(8px) brightness(50%)",
            WebkitBackdropFilter: "blur(8px) brightness(50%)",
            overflowX: "hidden",
          }}
        >
          {playerReady ? (
            status !== "finished" ? (
              <Button
                variant="default"
                leftSection={
                  status === "pending" ? <HeadphonesIcon /> : <PlayIcon />
                }
                classNames={{
                  root: classes.playButton,
                  inner: classes.playButtonInner,
                  section: classes.playButtonSection,
                }}
                mod={{ status }}
                onClick={() => {
                  const player = playerRef.current;
                  if (player) {
                    void player.play();
                  }
                }}
              >
                {status === "pending" ? (
                  <Box>
                    <Text inherit lh="xs">
                      Are you ready to listen? (5m)
                    </Text>
                    <Text size="xs" c="dimmed">
                      (Put on your headphones)
                    </Text>
                  </Box>
                ) : (
                  "I'm ready to continue"
                )}
              </Button>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <Button
                  variant="white"
                  onClick={onCompleted}
                  rightSection={<ContinueIcon />}
                >
                  Let&apos;s see how it works
                </Button>
              </motion.div>
            )
          ) : (
            <Loader type="bars" size="md" style={{ alignSelf: "center" }} />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default PatternsStoryPlayer;
